import TreasuryTable from './TreasuryTable';
import React, { useEffect, useState, useCallback } from 'react';
import { fetchAPI, postAPI } from '../../utils/utils';
import { can } from '../../utils/permissions';
import { getStripeCommissionInfo, getStripeCommissionInfoSync } from '../../utils/stripeCommission';
import {
    formatStripeDeadline,
    getGroupedPendingStripeRequirementMessages,
    hasPendingStripeRequirements,
    hasStripeRegistryRequirements,
} from '../../utils/stripeRequirements';
import './StripeConnectTab.css';
import { formatMoney } from '../../utils/money';

function StripeRegistryGuidance({ onChangeTab }) {
    const handleOpenRegistry = (event) => {
        if (onChangeTab && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
            event.preventDefault();
            onChangeTab('junta-legal');
        }
    };

    return (
        <div className="stripe-registry-guidance">
            <h3>Registre et pot ajudar amb la verificació</h3>
            <p>
                A <strong>Registre</strong> trobaràs ajuda per obtenir els documents que acrediten
                l&apos;associació i registrar els membres de la junta que cal declarar a Stripe.
            </p>
            <a className="stripe-registry-link" href="/quotes/junta-legal" onClick={handleOpenRegistry}>
                Anar a Registre
            </a>
        </div>
    );
}

/**
 * StripeConnectTab - Manages Stripe Connect configuration for the colla
 * 
 * States:
 * 1. Not configured - Show summary of fiscal data and activate button
 * 2. Pending verification - Show continue onboarding button
 * 3. Verified - Show status and dashboard link
 * 
 * Props:
 * - userInfo: Current user information
 * - paymentInfo: Fiscal data from "Dades fiscals" tab (IBAN, legal info, etc.)
 * - onChangeTab: Function to switch tabs (e.g., to go to "Dades fiscals" tab)
 */
function StripeConnectTab({ userInfo, paymentInfo, onChangeTab }) {
    const isDevelopment = process.env.NODE_ENV === 'development';

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Toggle loading state
    const [toggleLoading, setToggleLoading] = useState(false);
    
    // Dashboard preview data
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    
    // Revenue by event data
    const [revenueData, setRevenueData] = useState(null);
    const [revenueLoading, setRevenueLoading] = useState(false);
    const [revenueError, setRevenueError] = useState(null);
    const [expandedEvents, setExpandedEvents] = useState({});

    // Revenue by quota data
    const [quotaRevenueData, setQuotaRevenueData] = useState(null);
    const [quotaRevenueLoading, setQuotaRevenueLoading] = useState(false);
    const [quotaRevenueError, setQuotaRevenueError] = useState(null);
    const [expandedQuotas, setExpandedQuotas] = useState({});

    // Revenue by marxandatge data
    const [merxandatgeRevenueData, setMerxandatgeRevenueData] = useState(null);
    const [merxandatgeRevenueLoading, setMerxandatgeRevenueLoading] = useState(false);
    const [merxandatgeRevenueError, setMerxandatgeRevenueError] = useState(null);
    const [expandedOrders, setExpandedOrders] = useState({});

    // Revenue by subscription data
    const [subscriptionRevenueData, setSubscriptionRevenueData] = useState(null);
    const [subscriptionRevenueLoading, setSubscriptionRevenueLoading] = useState(false);
    const [subscriptionRevenueError, setSubscriptionRevenueError] = useState(null);
    const [expandedSubscriptions, setExpandedSubscriptions] = useState({});

    // Aggregated payout data
    const [payoutSummary, setPayoutSummary] = useState(null);
    const [payoutSummaryLoading, setPayoutSummaryLoading] = useState(false);
    const [runPayoutLoading, setRunPayoutLoading] = useState(false);

    // Commission info
    const [commissionInfo, setCommissionInfo] = useState(getStripeCommissionInfoSync());

    const [onboardingLoading, setOnboardingLoading] = useState(false);

    // Fetch commission info on mount
    useEffect(() => {
        getStripeCommissionInfo().then(setCommissionInfo);
    }, []);

    const fetchRevenueByEvent = useCallback(() => {
        setRevenueLoading(true);
        setRevenueError(null);
        fetchAPI('/stripe/connect/revenue-by-event', (data) => {
            if (data?.success) {
                setRevenueData(data);
            } else {
                setRevenueData(null);
                setRevenueError(data?.message || 'No s\'han pogut carregar els ingressos per esdeveniment');
            }
            setRevenueLoading(false);
        }, false, (error) => {
            setRevenueData(null);
            setRevenueError(error?.message || 'No s\'han pogut carregar els ingressos per esdeveniment');
            setRevenueLoading(false);
        });
    }, []);

    const fetchRevenueByQuota = useCallback(() => {
        setQuotaRevenueLoading(true);
        setQuotaRevenueError(null);
        fetchAPI('/stripe/connect/revenue-by-quota', (data) => {
            if (data?.success) {
                setQuotaRevenueData(data);
            } else {
                setQuotaRevenueData(null);
                setQuotaRevenueError(data?.message || 'No s\'han pogut carregar els ingressos per quota');
            }
            setQuotaRevenueLoading(false);
        }, false, (error) => {
            setQuotaRevenueData(null);
            setQuotaRevenueError(error?.message || 'No s\'han pogut carregar els ingressos per quota');
            setQuotaRevenueLoading(false);
        });
    }, []);

    const fetchRevenueByMerxandatge = useCallback(() => {
        setMerxandatgeRevenueLoading(true);
        setMerxandatgeRevenueError(null);
        fetchAPI('/stripe/connect/revenue-by-merxandatge', (data) => {
            if (data?.success) {
                setMerxandatgeRevenueData(data);
            } else {
                setMerxandatgeRevenueData(null);
                setMerxandatgeRevenueError(data?.message || 'No s\'han pogut carregar els ingressos per marxandatge');
            }
            setMerxandatgeRevenueLoading(false);
        }, false, (error) => {
            setMerxandatgeRevenueData(null);
            setMerxandatgeRevenueError(error?.message || 'No s\'han pogut carregar els ingressos per marxandatge');
            setMerxandatgeRevenueLoading(false);
        });
    }, []);

    const fetchRevenueBySubscription = useCallback(() => {
        setSubscriptionRevenueLoading(true);
        setSubscriptionRevenueError(null);
        fetchAPI('/stripe/connect/revenue-by-subscription', (data) => {
            if (data?.success) {
                setSubscriptionRevenueData(data);
            } else {
                setSubscriptionRevenueData(null);
                setSubscriptionRevenueError(data?.message || 'No s\'han pogut carregar els ingressos per subscripció');
            }
            setSubscriptionRevenueLoading(false);
        }, false, (error) => {
            setSubscriptionRevenueData(null);
            setSubscriptionRevenueError(error?.message || 'No s\'han pogut carregar els ingressos per subscripció');
            setSubscriptionRevenueLoading(false);
        });
    }, []);

    const fetchPayoutSummary = useCallback(() => {
        setPayoutSummaryLoading(true);
        fetchAPI('/stripe/connect/payouts/summary', (data) => {
            if (data?.success) {
                setPayoutSummary(data);
            } else {
                setPayoutSummary(null);
            }
            setPayoutSummaryLoading(false);
        }, false);
    }, []);

    const toggleEventExpanded = useCallback((eventId) => {
        setExpandedEvents(prev => ({
            ...prev,
            [eventId]: !prev[eventId]
        }));
    }, []);

    const toggleQuotaExpanded = useCallback((quotaId) => {
        setExpandedQuotas(prev => ({
            ...prev,
            [quotaId]: !prev[quotaId]
        }));
    }, []);

    const toggleOrderExpanded = useCallback((orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    }, []);

    const toggleSubscriptionExpanded = useCallback((quotaId) => {
        setExpandedSubscriptions(prev => ({
            ...prev,
            [quotaId]: !prev[quotaId]
        }));
    }, []);

    const fetchDashboardPreview = useCallback(() => {
        setDashboardLoading(true);
        fetchAPI('/stripe/connect/dashboard-preview', (data) => {
            if (data?.success) {
                setDashboardData(data);
            }
            setDashboardLoading(false);
        }, true);
        // Also fetch revenue by event, quota, marxandatge, and subscription
        fetchRevenueByEvent();
        fetchRevenueByQuota();
        fetchRevenueByMerxandatge();
        fetchRevenueBySubscription();
        if (isDevelopment) {
            fetchPayoutSummary();
        }
    }, [fetchRevenueByEvent, fetchRevenueByQuota, fetchRevenueByMerxandatge, fetchRevenueBySubscription, fetchPayoutSummary, isDevelopment]);

    const fetchStatus = useCallback(() => {
        setLoading(true);
        setError(null);
        fetchAPI('/stripe/connect/status', (data) => {
            if (data?.success) {
                setStatus(data);
                if (data.verified) {
                    fetchDashboardPreview();
                }
            } else {
                setError(data?.message || 'Error carregant la configuració de pagaments');
            }
            setLoading(false);
        });
    }, [fetchDashboardPreview]);

    useEffect(() => {
        fetchStatus();

        // Check URL params for onboarding return
        const urlParams = new URLSearchParams(window.location.search);
        const stripeParam = urlParams.get('stripe');
        if (stripeParam === 'complete' || stripeParam === 'refresh') {
            // Remove the param from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            // Refresh status
            fetchStatus();
        }
    }, [fetchStatus]);

    // Helper function to check if IBAN is the default placeholder or missing
    const isIbanMissing = () => {
        if (!paymentInfo?.iban) return true;
        const iban = paymentInfo.iban;
        const normalizedIban = iban.replace(/[\s-]/g, '').toUpperCase();
        return normalizedIban.match(/^ES[X0]{22}$/) || 
               normalizedIban === '' ||
               normalizedIban.includes('XXXX');
    };

    // Check what required fields are missing
    const getMissingFields = () => {
        const missing = [];
        if (!paymentInfo?.legalName?.trim()) missing.push('Raó social');
        if (!paymentInfo?.nif?.trim()) missing.push('NIF');
        if (!paymentInfo?.addressLine1?.trim()) missing.push('Adreça');
        if (!paymentInfo?.postalCode?.trim()) missing.push('Codi postal');
        if (!paymentInfo?.city?.trim()) missing.push('Ciutat');
        if (!paymentInfo?.companyPhone?.trim()) missing.push('Telèfon de l\'entitat');
        if (!paymentInfo?.repFirstName?.trim()) missing.push('Nom del representant');
        if (!paymentInfo?.repLastName?.trim()) missing.push('Cognoms del representant');
        if (!paymentInfo?.repDob) missing.push('Data de naixement del representant');
        if (!paymentInfo?.repAddressLine1?.trim()) missing.push('Adreça del representant');
        if (!paymentInfo?.repPostalCode?.trim()) missing.push('Codi postal del representant');
        if (!paymentInfo?.repCity?.trim()) missing.push('Ciutat del representant');
        if (!paymentInfo?.repEmail?.trim()) missing.push('Email del representant');
        if (!paymentInfo?.repPhone?.trim()) missing.push('Telèfon del representant');
        if (isIbanMissing()) missing.push('IBAN');
        return missing;
    };

    const missingFields = getMissingFields();
    const hasAllRequiredData = missingFields.length === 0;

    const handleActivate = async () => {
        if (!hasAllRequiredData) return;
        
        setActionLoading(true);
        setError(null);

        // Use data from paymentInfo
        const submitData = {
            businessName: paymentInfo.businessName || '',
            legalName: paymentInfo.legalName || '',
            nif: paymentInfo.nif || '',
            addressLine1: paymentInfo.addressLine1 || '',
            postalCode: paymentInfo.postalCode || '',
            city: paymentInfo.city || '',
            province: paymentInfo.province || '',
            email: paymentInfo.email || '',
            companyPhone: paymentInfo.companyPhone || '',
            iban: paymentInfo.iban || '',
            repFirstName: paymentInfo.repFirstName || '',
            repLastName: paymentInfo.repLastName || '',
            repDob: paymentInfo.repDob || '',
            repAddressLine1: paymentInfo.repAddressLine1 || '',
            repPostalCode: paymentInfo.repPostalCode || '',
            repCity: paymentInfo.repCity || '',
            repEmail: paymentInfo.repEmail || '',
            repPhone: paymentInfo.repPhone || '',
            repNationality: paymentInfo.repNationality || 'ES'
        };

        postAPI('/stripe/connect/create-account', submitData, (data) => {
            setActionLoading(false);
            if (data?.success && data?.onboardingUrl) {
                // Redirect to Stripe onboarding
                window.location.href = data.onboardingUrl;
            } else {
                // Show detailed error message including Stripe error if available
                const errorMsg = data?.error
                    ? `${data?.message || 'Error creant el compte'}: ${data.error}`
                    : (data?.message || 'Error creant el compte de pagaments');
                setError(errorMsg);
            }
        });
    };

    const handleContinueOnboarding = async () => {
        setActionLoading(true);
        setError(null);

        postAPI('/stripe/connect/refresh-onboarding', {}, (data) => {
            setActionLoading(false);
            if (data?.success && data?.onboardingUrl) {
                window.location.href = data.onboardingUrl;
            } else {
                setError(data?.message || 'Error generant l\'enllaç d\'onboarding');
            }
        });
    };

    const handleOpenDashboard = async () => {
        setActionLoading(true);
        setError(null);

        postAPI('/stripe/connect/dashboard-link', {}, (data) => {
            setActionLoading(false);
            if (data?.success && data?.url) {
                window.open(data.url, '_blank');
            } else {
                setError(data?.message || 'Error obrint el panell de gestió');
            }
        });
    };

    const handleToggle = (field, newValue) => {
        // Build confirmation message based on what's being toggled
        let message = '';
        if (field === 'disabledAll') {
            message = newValue 
                ? 'Estàs segur que vols desactivar TOTS els pagaments amb targeta? Els socis no podran pagar quotes, comprar a la botiga ni pagar formularis amb targeta.'
                : 'Vols reactivar els pagaments amb targeta?';
        } else if (field === 'disabledQuotes') {
            message = newValue
                ? 'Estàs segur que vols desactivar els pagaments de quotes amb targeta?'
                : 'Vols reactivar els pagaments de quotes amb targeta?';
        } else if (field === 'disabledMerxandatge') {
            message = newValue
                ? 'Estàs segur que vols desactivar els pagaments de la botiga amb targeta?'
                : 'Vols reactivar els pagaments de la botiga amb targeta?';
        } else if (field === 'disabledForms') {
            message = newValue
                ? 'Estàs segur que vols desactivar els pagaments de formularis d\'esdeveniments amb targeta?'
                : 'Vols reactivar els pagaments de formularis d\'esdeveniments amb targeta?';
        }

        if (!window.confirm(message)) {
            return;
        }

        setToggleLoading(true);
        setError(null);

        postAPI('/stripe/connect/toggle', { [field]: newValue }, (data) => {
            setToggleLoading(false);
            if (data?.success) {
                // Update status with new values
                setStatus(prev => ({
                    ...prev,
                    disabledAll: data.disabledAll,
                    disabledQuotes: data.disabledQuotes,
                    disabledMerxandatge: data.disabledMerxandatge,
                    disabledForms: data.disabledForms,
                    quotesEnabled: data.quotesEnabled,
                    merxandatgeEnabled: data.merxandatgeEnabled,
                    formsEnabled: data.formsEnabled
                }));
            } else {
                setError(data?.message || 'Error actualitzant la configuració');
            }
        });
    };

    // Redirect to Stripe onboarding to complete verification
    const handleRefreshOnboarding = () => {
        setOnboardingLoading(true);
        setError(null);
        postAPI('/stripe/connect/refresh-onboarding', {}, (data) => {
            setOnboardingLoading(false);
            if (data?.success && data?.onboardingUrl) {
                window.open(data.onboardingUrl, '_blank');
            } else {
                setError(data?.message || 'No s\'ha pogut generar l\'enllaç. Torna-ho a provar.');
            }
        });
    };

    const canManage = can(userInfo, 'MANAGE_QUOTES');
    const pendingRequirementGroups = getGroupedPendingStripeRequirementMessages(status?.requirements);
    const stripeDeadlineLabel = formatStripeDeadline(status?.requirements);
    const needsRegistryGuidance = hasStripeRegistryRequirements(status?.requirements);

    const handleRunAggregatedPayouts = () => {
        if (!canManage) return;

        if (!window.confirm('Vols executar ara el payout agregat manual?')) {
            return;
        }

        setRunPayoutLoading(true);
        setError(null);

        postAPI('/stripe/connect/payouts/run', {}, (data) => {
            setRunPayoutLoading(false);
            if (data?.success) {
                fetchDashboardPreview();
            } else {
                setError(data?.message || 'Error executant el payout agregat');
            }
        });
    };

    if (loading) {
        return (
            <div className="stripe-connect-section">
                <div className="stripe-loading">
                    <div className="spinner"></div>
                    <p>Carregant configuració de pagaments...</p>
                </div>
            </div>
        );
    }

    // Not configured state - show summary and activate button
    // (also when Stripe no longer has the account: activating creates a new one)
    if (!status?.configured || status?.accountInvalid) {
        return (
            <div className="stripe-connect-section">
                <div className="stripe-card not-configured wide">
                    <div className="stripe-header-row">
                        <div className="stripe-icon">💳</div>
                        <div>
                            <h2>Activar pagaments amb targeta</h2>
                            {status?.accountInvalid ? (
                                <p className="stripe-description">
                                    El compte de Stripe que teníeu ja no existeix. Activa'ls de nou per crear-ne un altre.
                                </p>
                            ) : null}
                            <div className="stripe-onboarding-images">
                                <img src={require('./imgs/pagaments-amb-targeta.jpg')} alt="Pagaments amb targeta" className="stripe-onboarding-img" />
                            </div>
                            <p className="stripe-description">
                                Permet als socis pagar quotes amb targeta de crèdit/dèbit. Els diners es transfereixen automàticament al compte de la colla.
                            </p>
                            <p className="stripe-commission-info">
                                <strong>Comissió:</strong> {commissionInfo.description} per transacció
                                <span className="commission-note"> (per defecte la paga l'usuari, no la colla)</span>
                            </p>
                        </div>
                    </div>

                    {/* Warning if data is missing */}
                    {!hasAllRequiredData ? (
                        <div className="stripe-data-warning">
                            <div className="warning-icon">⚠️</div>
                            <div className="warning-content">
                                <h4>Cal completar les dades fiscals</h4>
                                <p>Per activar els pagaments amb targeta, primer has de completar totes les dades fiscals de la colla.</p>
                                <div className="missing-fields">
                                    <strong>Camps pendents:</strong>
                                    <ul>
                                        {missingFields.map((field, i) => (
                                            <li key={i}>{field}</li>
                                        ))}
                                    </ul>
                                </div>
                                {onChangeTab ? (
                                    <button 
                                        className="warning-action-button"
                                        onClick={() => onChangeTab('info')}
                                    >
                                        Anar a Dades fiscals →
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Summary of data that will be used */}
                            <div className="stripe-data-summary">
                                <h4>Dades que s'enviaran a Stripe</h4>
                                
                                <div className="summary-section">
                                    <div className="summary-row">
                                        <span className="summary-label">Raó social:</span>
                                        <span className="summary-value">{paymentInfo.legalName}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">NIF:</span>
                                        <span className="summary-value">{paymentInfo.nif}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Adreça:</span>
                                        <span className="summary-value">{paymentInfo.addressLine1}, {paymentInfo.postalCode} {paymentInfo.city}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Telèfon entitat:</span>
                                        <span className="summary-value">{paymentInfo.companyPhone}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">IBAN:</span>
                                        <span className="summary-value">{paymentInfo.iban}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span className="summary-label">Representant:</span>
                                        <span className="summary-value">{paymentInfo.repFirstName} {paymentInfo.repLastName} ({paymentInfo.repEmail})</span>
                                    </div>
                                </div>

                                {onChangeTab ? (
                                    <button 
                                        className="edit-data-link"
                                        onClick={() => onChangeTab('info')}
                                    >
                                        ✏️ Modificar dades
                                    </button>
                                ) : null}
                            </div>

                            {/* Info about document verification requirement */}
                            <div className="document-info-section">
                                <h4>📄 Tindràs la targeta NIF a mà?</h4>
                                <p className="document-info-description">
                                    Stripe pot demanar-te la <strong>targeta NIF</strong> de l'associació o el <strong>certificat d'inscripció 
                                    al registre d'associacions</strong> per completar la verificació. 
                                    Tingues-los preparats en format PDF, JPG o PNG.
                                </p>
                            </div>

                            {error ? (
                                <div className="stripe-error">{error}</div>
                            ) : null}

                            {canManage ? (
                                <div className="stripe-actions">
                                    <button 
                                        className="stripe-activate-button"
                                        onClick={handleActivate}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Processant...' : 'Activar pagaments amb targeta →'}
                                    </button>
                                    <p className="stripe-hint">
                                        Seràs redirigit a Stripe per completar la verificació i acceptar els termes de servei.
                                    </p>
                                </div>
                            ) : (
                                <p className="stripe-no-permission">
                                    Necessites permisos de gestió de quotes per activar els pagaments.
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Toggle settings preview (disabled) */}
                {canManage ? (
                    <div className="dashboard-preview-card stripe-toggle-settings not-enabled">
                        <h3>⚙️ Configuració de pagaments amb targeta</h3>

                        <div className="stripe-toggle-list">
                            <div className="stripe-toggle-item">
                                <div className="toggle-info">
                                    <span className="toggle-label">Tots els pagaments activats</span>
                                    <span className="toggle-description">
                                        Permet pagaments amb targeta a l'app
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Quotes</span>
                                    <span className="toggle-description">
                                        Pagament de quotes de soci
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Botiga / Marxandatge</span>
                                    <span className="toggle-description">
                                        Compres de productes a la botiga
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Formularis d'esdeveniments</span>
                                    <span className="toggle-description">
                                        Pagaments dins formularis (inscripcions, activitats...)
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    // Pending verification state
    if (!status.verified) {
        return (
            <div className="stripe-connect-section">
                <div className="stripe-card pending">
                    <div className="stripe-icon">⏳</div>
                    <h2>Verificació pendent</h2>
                    <p className="stripe-description">
                        El compte de pagaments està creat però cal completar la verificació per poder rebre pagaments.
                    </p>

                    <div className="status-details">
                        <div className="status-row">
                            <span className="status-label">Compte:</span>
                            <span className="status-value">{status.accountId}</span>
                        </div>
                        <div className="status-row">
                            <span className="status-label">Pagaments:</span>
                            <span className={`status-badge ${status.chargesEnabled ? 'enabled' : 'disabled'}`}>
                                {status.chargesEnabled ? '✓ Activats' : '✗ Pendents'}
                            </span>
                        </div>
                        <div className="status-row">
                            <span className="status-label">Transferències:</span>
                            <span className={`status-badge ${status.payoutsEnabled ? 'enabled' : 'disabled'}`}>
                                {status.payoutsEnabled ? '✓ Activades' : '✗ Pendents'}
                            </span>
                        </div>
                        <div className="status-row">
                            <span className="status-label">Comissió:</span>
                            <span className="status-value commission-value">{commissionInfo.description} per transacció <span className="commission-note">(per defecte la paga l'usuari)</span></span>
                        </div>
                    </div>

                    {pendingRequirementGroups.length > 0 ? (
                        <div className="stripe-requirements-list">
                            {stripeDeadlineLabel ? (
                                <p className="requirements-deadline">
                                    Data límit: <strong>{stripeDeadlineLabel}</strong>
                                </p>
                            ) : null}
                            {pendingRequirementGroups.map(group => (
                                <div key={group.type} className={`requirements-info compact ${group.type}`}>
                                    <div className="requirements-group-label">{group.label}</div>
                                    <ul>
                                        {group.items.map((item, i) => (
                                            <li key={`${group.type}-${item.key || i}`}>
                                                <span className="requirement-message-label">{item.label}</span>
                                                {item.detail ? (
                                                    <span className="requirement-message-detail">{item.detail}</span>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {needsRegistryGuidance ? <StripeRegistryGuidance onChangeTab={onChangeTab} /> : null}

                    {error ? (
                        <div className="stripe-error">{error}</div>
                    ) : null}

                    {canManage ? (
                        <div className="stripe-pending-actions">
                            <button
                                className="stripe-continue-button"
                                onClick={handleContinueOnboarding}
                                disabled={actionLoading}
                            >
                                {actionLoading ? 'Processant...' : 'Continuar verificació a Stripe'}
                            </button>
                            <p className="stripe-hint">
                                {needsRegistryGuidance
                                    ? 'Quan tinguis la documentació i les dades de la junta a punt, continua la verificació a Stripe.'
                                    : 'Completa les dades pendents al portal de Stripe.'}
                            </p>
                        </div>
                    ) : null}
                </div>

                {/* Toggle settings preview (disabled) */}
                {canManage ? (
                    <div className="dashboard-preview-card stripe-toggle-settings not-enabled">
                        <h3>⚙️ Configuració de pagaments amb targeta</h3>

                        <div className="stripe-toggle-list">
                            <div className="stripe-toggle-item">
                                <div className="toggle-info">
                                    <span className="toggle-label">Tots els pagaments activats</span>
                                    <span className="toggle-description">
                                        Permet pagaments amb targeta a l'app
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Quotes</span>
                                    <span className="toggle-description">
                                        Pagament de quotes de soci
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Botiga / Marxandatge</span>
                                    <span className="toggle-description">
                                        Compres de productes a la botiga
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                            <div className="stripe-toggle-item sub-toggle">
                                <div className="toggle-info">
                                    <span className="toggle-label">Formularis d'esdeveniments</span>
                                    <span className="toggle-description">
                                        Pagaments dins formularis (inscripcions, activitats...)
                                    </span>
                                </div>
                                <label className="toggle-switch">
                                    <input type="checkbox" checked disabled />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    // Verified state
    return (
        <div className="stripe-connect-section verified-section">
            {/* Warning for pending requirements even when verified */}
            {hasPendingStripeRequirements(status?.requirements) && canManage ? (
                <div className="dashboard-preview-card stripe-pending-requirements-warning">
                    <div className="pending-requirements-content">
                        <span className="warning-icon">⚠️</span>
                        <div>
                            <h3>Requisits pendents de Stripe</h3>
                            <p>
                                Stripe demana informació addicional per completar la verificació del compte. 
                                Si no es completen, els pagaments podrien ser pausats.
                            </p>
                            {stripeDeadlineLabel ? (
                                <p className="requirements-deadline in-warning">
                                    Data límit: <strong>{stripeDeadlineLabel}</strong>
                                </p>
                            ) : null}
                        </div>
                    </div>

                    {pendingRequirementGroups.length > 0 ? (
                        <div className="stripe-requirements-list in-warning">
                            {pendingRequirementGroups.map(group => (
                                <div key={group.type} className={`requirements-info compact ${group.type}`}>
                                    <div className="requirements-group-label">{group.label}</div>
                                    <ul>
                                        {group.items.map((item, i) => (
                                            <li key={`${group.type}-${item.key || i}`}>
                                                <span className="requirement-message-label">{item.label}</span>
                                                {item.detail ? (
                                                    <span className="requirement-message-detail">{item.detail}</span>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {needsRegistryGuidance ? <StripeRegistryGuidance onChangeTab={onChangeTab} /> : null}
                    <div className="stripe-pending-actions">
                        <button
                            className="stripe-continue-button"
                            onClick={handleRefreshOnboarding}
                            disabled={onboardingLoading}
                        >
                            {onboardingLoading ? 'Carregant...' : 'Completar verificació a Stripe'}
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Only show dashboards if payments are not globally disabled */}
            {!status?.disabledAll ? (
                <>
            {/* Weekly Chart */}
            {dashboardLoading ? (
                <div className="dashboard-preview-card">
                    <div className="stripe-loading-inline">
                        <div className="spinner small"></div>
                        <span>Carregant dades...</span>
                    </div>
                </div>
            ) : dashboardData?.weeklyData?.length > 0 ? (
                <div className="dashboard-preview-card">
                    <h3>📊 Ingressos per setmana</h3>
                    <div className="weekly-chart">
                        {dashboardData.weeklyData.map((week, i) => {
                            const maxAmount = Math.max(...dashboardData.weeklyData.map(w => w.amount), 1);
                            const heightPercent = (week.amount / maxAmount) * 100;
                            return (
                                <div key={i} className="chart-bar-container">
                                    <div 
                                        className="chart-bar" 
                                        style={{ height: `${Math.max(heightPercent, 2)}%` }}
                                        title={`${formatMoney(week.amount)} €`}
                                    >
                                        {week.amount > 0 ? (
                                            <span className="bar-amount">{week.amount.toFixed(0)}€</span>
                                        ) : null}
                                    </div>
                                    <span className="chart-label">{week.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {/* Revenue by Event */}
            {revenueLoading ? (
                <div className="dashboard-preview-card">
                    <div className="stripe-loading-inline">
                        <div className="spinner small"></div>
                        <span>Carregant ingressos per esdeveniment...</span>
                    </div>
                </div>
            ) : revenueData?.revenueByEvent?.length > 0 ? (
                <div className="dashboard-preview-card revenue-by-event-card">
                    <h3>📋 Ingressos per esdeveniment</h3>
                    
                    {/* Summary */}
                    <div className="revenue-summary">
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{formatMoney(revenueData.summary.totalRevenue)} €</span>
                            <span className="revenue-summary-label">Total ingressat</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{revenueData.summary.totalPayments}</span>
                            <span className="revenue-summary-label">Pagaments</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{revenueData.summary.eventCount}</span>
                            <span className="revenue-summary-label">Esdeveniments</span>
                        </div>
                    </div>

                    {/* Events Table */}
                    <TreasuryTable label="Ingressos per esdeveniment" className="revenue-table-container" identityColumn={2}>
                        <table className="revenue-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Esdeveniment</th>
                                    <th className="text-right">Import</th>
                                    <th className="text-center">Pagaments</th>
                                    <th className="text-right">Últim pagament</th>
                                </tr>
                            </thead>
                            <tbody>
                                {revenueData.revenueByEvent.map(event => {
                                    const isExpanded = expandedEvents[event.eventId];
                                    return (
                                        <React.Fragment key={event.eventId}>
                                            <tr 
                                                className={`event-row ${isExpanded ? 'expanded' : ''}`}
                                                onClick={() => toggleEventExpanded(event.eventId)}
                                            >
                                                <td className="expand-cell">
                                                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                                        ▶
                                                    </span>
                                                </td>
                                                <td className="event-title-cell">
                                                    <span className="event-title">{event.eventTitle}</span>
                                                </td>
                                                <td className="text-right amount-cell">
                                                    <span className="event-amount">{formatMoney(event.totalAmount)} €</span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="payment-count">{event.paymentCount}</span>
                                                </td>
                                                <td className="text-right date-cell">
                                                    <span className="last-payment-date">
                                                        {new Date(event.lastPayment).toLocaleDateString('ca-ES', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                            {isExpanded && event.payments?.length > 0 ? (
                                                <tr className="payments-detail-row">
                                                    <td colSpan="5">
                                                        <div className="payments-detail-container">
                                                            {event.payments.map(payment => (
                                                                <div key={payment.id} className="payment-detail-item">
                                                                    <div className="payment-detail-main">
                                                                        <span className="payment-detail-user">
                                                                            {payment.userName}
                                                                        </span>
                                                                        {payment.description ? (
                                                                            <span className="payment-detail-description">
                                                                                {payment.description}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="payment-detail-amount">
                                                                        {formatMoney(payment.amount)} €
                                                                    </span>
                                                                    <span className="payment-detail-date">
                                                                        {new Date(payment.date).toLocaleDateString('ca-ES', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </TreasuryTable>
                </div>
            ) : revenueError ? (
                <div className="dashboard-preview-card">
                    <h3>📋 Ingressos per esdeveniment</h3>
                    <div className="no-payments-message">
                        <span>⚠️</span>
                        <p>{revenueError}</p>
                    </div>
                </div>
            ) : revenueData ? (
                <div className="dashboard-preview-card">
                    <h3>📋 Ingressos per esdeveniment</h3>
                    <div className="no-payments-message">
                        <span>📭</span>
                        <p>Encara no hi ha ingressos per esdeveniment</p>
                    </div>
                </div>
            ) : null}

            {/* Revenue by Quota */}
            {quotaRevenueLoading ? (
                <div className="dashboard-preview-card">
                    <div className="stripe-loading-inline">
                        <div className="spinner small"></div>
                        <span>Carregant ingressos per quota...</span>
                    </div>
                </div>
            ) : quotaRevenueData?.revenueByQuota?.length > 0 ? (
                <div className="dashboard-preview-card revenue-by-quota-card">
                    <h3>💰 Ingressos per quota</h3>
                    
                    {/* Summary */}
                    <div className="revenue-summary">
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{formatMoney(quotaRevenueData.summary.totalRevenue)} €</span>
                            <span className="revenue-summary-label">Total ingressat</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{quotaRevenueData.summary.totalPayments}</span>
                            <span className="revenue-summary-label">Pagaments</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{quotaRevenueData.summary.quotaCount}</span>
                            <span className="revenue-summary-label">Quotes</span>
                        </div>
                    </div>

                    {/* Quotas Table */}
                    <TreasuryTable label="Ingressos per quota" className="revenue-table-container" identityColumn={2}>
                        <table className="revenue-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Quota</th>
                                    <th className="text-right">Import</th>
                                    <th className="text-center">Pagaments</th>
                                    <th className="text-right">Últim pagament</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotaRevenueData.revenueByQuota.map(quota => {
                                    const isExpanded = expandedQuotas[quota.quotaId];
                                    return (
                                        <React.Fragment key={quota.quotaId}>
                                            <tr 
                                                className={`event-row ${isExpanded ? 'expanded' : ''}`}
                                                onClick={() => toggleQuotaExpanded(quota.quotaId)}
                                            >
                                                <td className="expand-cell">
                                                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                                        ▶
                                                    </span>
                                                </td>
                                                <td className="event-title-cell">
                                                    <span className="event-title">{quota.quotaTitle}</span>
                                                    {quota.quotaType ? (
                                                        <span className="quota-type-badge">{quota.quotaType}</span>
                                                    ) : null}
                                                </td>
                                                <td className="text-right amount-cell">
                                                    <span className="event-amount">{formatMoney(quota.totalAmount)} €</span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="payment-count">{quota.paymentCount}</span>
                                                </td>
                                                <td className="text-right date-cell">
                                                    <span className="last-payment-date">
                                                        {new Date(quota.lastPayment).toLocaleDateString('ca-ES', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                            {isExpanded && quota.payments?.length > 0 ? (
                                                <tr className="payments-detail-row">
                                                    <td colSpan="5">
                                                        <div className="payments-detail-container">
                                                            {quota.payments.map(payment => (
                                                                <div key={payment.id} className="payment-detail-item">
                                                                    <div className="payment-detail-main">
                                                                        <span className="payment-detail-user">
                                                                            {payment.userName}
                                                                        </span>
                                                                        {payment.description ? (
                                                                            <span className="payment-detail-description">
                                                                                {payment.description}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="payment-detail-amount">
                                                                        {formatMoney(payment.amount)} €
                                                                    </span>
                                                                    <span className="payment-detail-date">
                                                                        {new Date(payment.date).toLocaleDateString('ca-ES', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </TreasuryTable>
                </div>
            ) : quotaRevenueError ? (
                <div className="dashboard-preview-card">
                    <h3>💰 Ingressos per quota</h3>
                    <div className="no-payments-message">
                        <span>⚠️</span>
                        <p>{quotaRevenueError}</p>
                    </div>
                </div>
            ) : quotaRevenueData ? (
                <div className="dashboard-preview-card">
                    <h3>💰 Ingressos per quota</h3>
                    <div className="no-payments-message">
                        <span>📭</span>
                        <p>Encara no hi ha ingressos per quota</p>
                    </div>
                </div>
            ) : null}

            {/* Revenue by Marxandatge */}
            {merxandatgeRevenueLoading ? (
                <div className="dashboard-preview-card">
                    <div className="stripe-loading-inline">
                        <div className="spinner small"></div>
                        <span>Carregant ingressos per marxandatge...</span>
                    </div>
                </div>
            ) : merxandatgeRevenueData?.revenueByMerxandatge?.length > 0 ? (
                <div className="dashboard-preview-card revenue-by-merxandatge-card">
                    <h3>🛍️ Ingressos per marxandatge</h3>
                    
                    {/* Summary */}
                    <div className="revenue-summary">
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{formatMoney(merxandatgeRevenueData.summary.totalRevenue)} €</span>
                            <span className="revenue-summary-label">Total ingressat</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{merxandatgeRevenueData.summary.totalPayments}</span>
                            <span className="revenue-summary-label">Pagaments</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{merxandatgeRevenueData.summary.orderCount}</span>
                            <span className="revenue-summary-label">Comandes</span>
                        </div>
                    </div>

                    {/* Orders Table */}
                    <TreasuryTable label="Ingressos per marxandatge" className="revenue-table-container" identityColumn={2}>
                        <table className="revenue-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Comprador</th>
                                    <th className="text-right">Import</th>
                                    <th className="text-center">Articles</th>
                                    <th className="text-right">Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {merxandatgeRevenueData.revenueByMerxandatge.map(order => {
                                    const isExpanded = expandedOrders[order.orderId];
                                    return (
                                        <React.Fragment key={order.orderId}>
                                            <tr 
                                                className={`event-row ${isExpanded ? 'expanded' : ''}`}
                                                onClick={() => toggleOrderExpanded(order.orderId)}
                                            >
                                                <td className="expand-cell">
                                                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                                        ▶
                                                    </span>
                                                </td>
                                                <td className="event-title-cell">
                                                    <span className="event-title">
                                                        {order.buyerName || 'Anònim'}
                                                        {order.itemNames?.length > 0 ? (
                                                            <span className="order-items-preview">
                                                                {': '}
                                                                {order.itemNames.join(', ').length > 40 
                                                                    ? order.itemNames.join(', ').substring(0, 40) + '...'
                                                                    : order.itemNames.join(', ')}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                </td>
                                                <td className="text-right amount-cell">
                                                    <span className="event-amount">{formatMoney(order.totalAmount)} €</span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="payment-count">{order.itemCount || '-'}</span>
                                                </td>
                                                <td className="text-right date-cell">
                                                    <span className="last-payment-date">
                                                        {new Date(order.lastPayment).toLocaleDateString('ca-ES', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                            {isExpanded && order.payments?.length > 0 ? (
                                                <tr className="payments-detail-row">
                                                    <td colSpan="5">
                                                        <div className="payments-detail-container">
                                                            {order.payments.map(payment => (
                                                                <div key={payment.id} className="payment-detail-item">
                                                                    <div className="payment-detail-main">
                                                                        <span className="payment-detail-user">
                                                                            {payment.userName}
                                                                        </span>
                                                                        {payment.description ? (
                                                                            <span className="payment-detail-description">
                                                                                {payment.description}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="payment-detail-amount">
                                                                        {formatMoney(payment.amount)} €
                                                                    </span>
                                                                    <span className="payment-detail-date">
                                                                        {new Date(payment.date).toLocaleDateString('ca-ES', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </TreasuryTable>
                </div>
            ) : merxandatgeRevenueError ? (
                <div className="dashboard-preview-card">
                    <h3>🛍️ Ingressos per marxandatge</h3>
                    <div className="no-payments-message">
                        <span>⚠️</span>
                        <p>{merxandatgeRevenueError}</p>
                    </div>
                </div>
            ) : merxandatgeRevenueData ? (
                <div className="dashboard-preview-card">
                    <h3>🛍️ Ingressos per marxandatge</h3>
                    <div className="no-payments-message">
                        <span>📭</span>
                        <p>Encara no hi ha ingressos per marxandatge</p>
                    </div>
                </div>
            ) : null}

            {/* Revenue by Subscription */}
            {subscriptionRevenueLoading ? (
                <div className="dashboard-preview-card">
                    <div className="stripe-loading-inline">
                        <div className="spinner small"></div>
                        <span>Carregant ingressos per subscripció...</span>
                    </div>
                </div>
            ) : subscriptionRevenueData?.revenueBySubscription?.length > 0 ? (
                <div className="dashboard-preview-card revenue-by-subscription-card">
                    <h3>🔄 Ingressos per subscripció</h3>
                    
                    {/* Summary */}
                    <div className="revenue-summary">
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{formatMoney(subscriptionRevenueData.summary.totalRevenue)} €</span>
                            <span className="revenue-summary-label">Total ingressat</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{subscriptionRevenueData.summary.totalPayments}</span>
                            <span className="revenue-summary-label">Pagaments</span>
                        </div>
                        <div className="revenue-summary-item">
                            <span className="revenue-summary-value">{subscriptionRevenueData.summary.activeSubscribers}</span>
                            <span className="revenue-summary-label">Subscriptors actius</span>
                        </div>
                        {subscriptionRevenueData.summary.totalDonations > 0 ? (
                            <div className="revenue-summary-item">
                                <span className="revenue-summary-value">{formatMoney(subscriptionRevenueData.summary.totalDonations)} €</span>
                                <span className="revenue-summary-label">Donatius</span>
                            </div>
                        ) : null}
                    </div>

                    {/* Subscriptions Table */}
                    <TreasuryTable label="Ingressos per subscripció" className="revenue-table-container" identityColumn={2}>
                        <table className="revenue-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Subscripció</th>
                                    <th className="text-right">Import</th>
                                    <th className="text-center">Pagaments</th>
                                    <th className="text-right">Últim pagament</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subscriptionRevenueData.revenueBySubscription.map(subscription => {
                                    const isExpanded = expandedSubscriptions[subscription.quotaId];
                                    return (
                                        <React.Fragment key={subscription.quotaId}>
                                            <tr 
                                                className={`event-row ${isExpanded ? 'expanded' : ''}`}
                                                onClick={() => toggleSubscriptionExpanded(subscription.quotaId)}
                                            >
                                                <td className="expand-cell">
                                                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                                        ▶
                                                    </span>
                                                </td>
                                                <td className="event-title-cell">
                                                    <span className="event-title">{subscription.quotaTitle}</span>
                                                    <span className="subscriber-count-badge">
                                                        {subscription.subscriberCount} subscriptor{subscription.subscriberCount !== 1 ? 's' : ''}
                                                    </span>
                                                </td>
                                                <td className="text-right amount-cell">
                                                    <span className="event-amount">{formatMoney(subscription.totalAmount)} €</span>
                                                </td>
                                                <td className="text-center">
                                                    <span className="payment-count">{subscription.paymentCount}</span>
                                                </td>
                                                <td className="text-right date-cell">
                                                    <span className="last-payment-date">
                                                        {new Date(subscription.lastPayment).toLocaleDateString('ca-ES', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                            {isExpanded && subscription.payments?.length > 0 ? (
                                                <tr className="payments-detail-row">
                                                    <td colSpan="5">
                                                        <div className="payments-detail-container">
                                                            {subscription.payments.map(payment => (
                                                                <div key={payment.id} className="payment-detail-item">
                                                                    <div className="payment-detail-main">
                                                                        <span className="payment-detail-user">
                                                                            {payment.userName}
                                                                        </span>
                                                                        {payment.donationAmount > 0 ? (
                                                                            <span className="payment-detail-description">
                                                                                +{formatMoney(payment.donationAmount)} € donatiu
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="payment-detail-amount">
                                                                        {formatMoney(payment.amount)} €
                                                                    </span>
                                                                    <span className="payment-detail-date">
                                                                        {new Date(payment.date).toLocaleDateString('ca-ES', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </TreasuryTable>
                </div>
            ) : subscriptionRevenueError ? (
                <div className="dashboard-preview-card">
                    <h3>🔄 Ingressos per subscripció</h3>
                    <div className="no-payments-message">
                        <span>⚠️</span>
                        <p>{subscriptionRevenueError}</p>
                    </div>
                </div>
            ) : subscriptionRevenueData ? (
                <div className="dashboard-preview-card">
                    <h3>🔄 Ingressos per subscripció</h3>
                    <div className="no-payments-message">
                        <span>📭</span>
                        <p>Encara no hi ha ingressos per subscripció</p>
                    </div>
                </div>
            ) : null}
                </>
            ) : null}

            {isDevelopment ? (
                payoutSummaryLoading ? (
                    <div className="dashboard-preview-card">
                        <div className="stripe-loading-inline">
                            <div className="spinner small"></div>
                            <span>Carregant resum de payouts...</span>
                        </div>
                    </div>
                ) : payoutSummary ? (
                    <div className="dashboard-preview-card payout-aggregator-card">
                        <h3>🏦 Payouts agregats</h3>

                        <div className="payout-summary-grid">
                            <div className="payout-summary-item">
                                <span className="payout-summary-value">{formatMoney(payoutSummary.pending?.totalAmount || 0)} €</span>
                                <span className="payout-summary-label">Import pendent</span>
                            </div>
                            <div className="payout-summary-item">
                                <span className="payout-summary-value">{payoutSummary.pending?.totalPayments || 0}</span>
                                <span className="payout-summary-label">Pagaments pendents</span>
                            </div>
                            <div className="payout-summary-item">
                                <span className="payout-summary-value">{payoutSummary.pending?.totalBuckets || 0}</span>
                                <span className="payout-summary-label">Buckets pendents</span>
                            </div>
                            <div className="payout-summary-item">
                                <span className="payout-summary-value">
                                    {payoutSummary.nextEligibleRunAt
                                        ? new Date(payoutSummary.nextEligibleRunAt).toLocaleDateString('ca-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })
                                        : '-'}
                                </span>
                                <span className="payout-summary-label">Pròxima execució</span>
                            </div>
                        </div>

                        <div className="payout-type-breakdown">
                            <div className="payout-type-item">
                                <span>Esdeveniments</span>
                                <strong>{payoutSummary.pending?.byType?.event || 0}</strong>
                            </div>
                            <div className="payout-type-item">
                                <span>Quotes</span>
                                <strong>{payoutSummary.pending?.byType?.quota || 0}</strong>
                            </div>
                            <div className="payout-type-item">
                                <span>Marxandatge</span>
                                <strong>{payoutSummary.pending?.byType?.merxandatge || 0}</strong>
                            </div>
                        </div>

                        {payoutSummary.lastRun ? (
                            <p className="payout-last-run">
                                Últim run: {payoutSummary.lastRun.status} · {new Date(payoutSummary.lastRun.finished_at || payoutSummary.lastRun.started_at).toLocaleString('ca-ES')}
                            </p>
                        ) : (
                            <p className="payout-last-run">Encara no s'ha executat cap run de payouts.</p>
                        )}

                        {canManage ? (
                            <div className="payout-actions">
                                <button
                                    className="stripe-dashboard-button payout-run-button"
                                    onClick={handleRunAggregatedPayouts}
                                    disabled={runPayoutLoading}
                                >
                                    {runPayoutLoading ? 'Executant...' : 'Executar payout agregat ara'}
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null
            ) : null}

            {/* Toggle settings */}
            {canManage ? (
                <div className={`dashboard-preview-card stripe-toggle-settings ${!status?.chargesEnabled ? 'not-enabled' : ''}`}>
                    <h3>⚙️ Configuració de pagaments amb targeta</h3>
                    
                    <div className="stripe-toggle-list">
                        {/* Global toggle */}
                        <div className={`stripe-toggle-item ${status?.disabledAll ? 'disabled' : ''}`}>
                            <div className="toggle-info">
                                <span className="toggle-label">Tots els pagaments activats</span>
                                <span className="toggle-description">
                                    Permet pagaments amb targeta a l'app
                                </span>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!status?.disabledAll}
                                    onChange={(e) => handleToggle('disabledAll', !e.target.checked)}
                                    disabled={toggleLoading}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>

                        {/* Individual toggles - only show if not globally disabled */}
                        {!status?.disabledAll ? (
                            <>
                                <div className={`stripe-toggle-item sub-toggle ${status?.disabledQuotes ? 'disabled' : ''}`}>
                                    <div className="toggle-info">
                                        <span className="toggle-label">Quotes</span>
                                        <span className="toggle-description">
                                            Pagament de quotes de soci
                                        </span>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={!status?.disabledQuotes}
                                            onChange={(e) => handleToggle('disabledQuotes', !e.target.checked)}
                                            disabled={toggleLoading}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>

                                <div className={`stripe-toggle-item sub-toggle ${status?.disabledMerxandatge ? 'disabled' : ''}`}>
                                    <div className="toggle-info">
                                        <span className="toggle-label">Botiga / Marxandatge</span>
                                        <span className="toggle-description">
                                            Compres de productes a la botiga
                                        </span>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={!status?.disabledMerxandatge}
                                            onChange={(e) => handleToggle('disabledMerxandatge', !e.target.checked)}
                                            disabled={toggleLoading}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>

                                <div className={`stripe-toggle-item sub-toggle ${status?.disabledForms ? 'disabled' : ''}`}>
                                    <div className="toggle-info">
                                        <span className="toggle-label">Formularis d'esdeveniments</span>
                                        <span className="toggle-description">
                                            Pagaments dins formularis (inscripcions, activitats...)
                                        </span>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={!status?.disabledForms}
                                            onChange={(e) => handleToggle('disabledForms', !e.target.checked)}
                                            disabled={toggleLoading}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </>
                        ) : (
                            <div className="stripe-toggle-warning">
                                Els pagaments amb targeta estan completament desactivats.
                                Activa l'opció anterior per configurar tipus específics.
                            </div>
                        )}
                    </div>

                    {toggleLoading ? (
                        <div className="stripe-loading-inline toggle-loading">
                            <div className="spinner small"></div>
                            <span>Actualitzant...</span>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* Manage payments button */}
            {error ? (
                <div className="stripe-error">{error}</div>
            ) : null}
            
            <div className="stripe-manage-section">
                <button 
                    className="stripe-dashboard-button"
                    onClick={handleOpenDashboard}
                    disabled={actionLoading}
                >
                    {actionLoading ? 'Obrint...' : 'Gestionar pagaments a Stripe'}
                </button>
            </div>
        </div>
    );
}

export default StripeConnectTab;
